import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, RefreshCw, Receipt, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errText } from "@/lib/api";

export const BillingCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/status", { params: { probe: true } });
      setStatus(data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/sync");
      toast.success(
        `${data.created} new, ${data.updated} refreshed from the billing app`,
        data.skipped ? { description: `${data.skipped} row(s) could not be read` } : undefined,
      );
      load();
      window.dispatchEvent(new Event("diary-refresh"));
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <section className="surface p-6 lg:p-8" data-testid="billing-section">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <Receipt className="h-5 w-5" /> Chambers' Billing
      </h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        Matters are managed in the billing app and mirrored here every night, matched on case number.
        Fee notes are raised there — the buttons on a matter or a diary entry carry the details
        across.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="billing-status">
        <span
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm border text-sm font-semibold ${
            status.connected
              ? "border-border"
              : "border-destructive/40 text-destructive bg-destructive/5"
          }`}
        >
          {status.connected ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          {status.connected ? "Connected" : status.error || "Not connected"}
        </span>
        {status.base_url ? (
          <span className="text-sm text-muted-foreground font-mono truncate max-w-full">
            {status.base_url}
          </span>
        ) : null}
      </div>

      <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="label-caps">Mirrored matters</dt>
          <dd className="mt-1 text-lg font-semibold" data-testid="billing-linked-count">
            {status.linked_matters}
          </dd>
        </div>
        <div>
          <dt className="label-caps">Last sync</dt>
          <dd className="mt-1 text-lg font-semibold">
            {status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : "Never"}
          </dd>
        </div>
        {status.last_result ? (
          <div>
            <dt className="label-caps">Last result</dt>
            <dd className="mt-1 text-lg font-semibold">
              {status.last_result.created} new · {status.last_result.updated} updated
            </dd>
          </div>
        ) : null}
      </dl>

      {status.last_result?.unmatched_forums?.length ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="billing-unmatched-forums">
          No court in the diary matches{" "}
          <strong>{status.last_result.unmatched_forums.join(", ")}</strong> — add it under Courts and
          the next pull will colour those matters in.
        </p>
      ) : null}

      <div className="mt-6">
        <Button
          data-testid="billing-sync-btn"
          onClick={sync}
          disabled={busy || !status.configured}
          className="rounded-full h-12 px-6"
        >
          <RefreshCw className={`h-5 w-5 mr-2 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Pulling matters…" : "Pull matters now"}
        </Button>
        <p className="mt-3 text-xs text-muted-foreground">
          Pulled automatically every night around {String(status.sync_hour ?? 3).padStart(2, "0")}:00.
        </p>
      </div>
    </section>
  );
};
